/**
 * Simple EventEmitter for web
 *
 * Provides basic event emitter functionality for decoupled module communication.
 * Mirrors mobile's module-core/src/modules/contracts/event-emitter.ts
 */

type EventHandler<T = unknown> = (payload: T) => Promise<void> | void;

export default class EventEmitter<TEventMap extends Record<string, unknown>> {
    private listeners: Map<string, Set<EventHandler>> = new Map();

    on<K extends keyof TEventMap & string>(event: K, handler: EventHandler<TEventMap[K]>): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(handler as EventHandler);
    }

    off<K extends keyof TEventMap & string>(event: K, handler: EventHandler<TEventMap[K]>): void {
        const handlers = this.listeners.get(event);
        if (handlers) {
            handlers.delete(handler as EventHandler);
            if (handlers.size === 0) {
                this.listeners.delete(event);
            }
        }
    }

    emit<K extends keyof TEventMap & string>(event: K, payload: TEventMap[K]): void {
        const handlers = this.listeners.get(event);
        if (handlers) {
            for (const handler of handlers) {
                try {
                    handler(payload);
                } catch (error) {
                    console.error(`[EventEmitter] Error in handler for event "${event}":`, error);
                }
            }
        }
    }

    /**
     * Emit an event and await all handlers (including async ones).
     * Use this when the caller needs to wait for all handlers to complete
     * before proceeding (e.g., clearing data before page reload).
     */
    async emitAsync<K extends keyof TEventMap & string>(event: K, payload: TEventMap[K]): Promise<void> {
        const handlers = this.listeners.get(event);
        if (handlers) {
            const promises: Promise<void>[] = [];
            for (const handler of handlers) {
                try {
                    const result = handler(payload);
                    if (result instanceof Promise) {
                        promises.push(result);
                    }
                } catch (error) {
                    console.error(`[EventEmitter] Error in handler for event "${event}":`, error);
                }
            }
            await Promise.all(promises);
        }
    }

    removeAllListeners(): void {
        this.listeners.clear();
    }
}
